import { useState, useEffect } from "react";
import "@/App.css";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { Sidebar, MobileNav, visibleIds, GOVERNANCE_GROUP } from "@/components/Sidebar";
import { FilterBar } from "@/components/FilterBar";
import { ProjectHeader } from "@/components/ProjectHeader";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { getRole } from "@/lib/roles";
import { useLens, lensStore } from "@/lib/lensStore";
import { track } from "@/lib/track";
import Analytics from "@/pages/Analytics";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import InsightFeed from "@/pages/InsightFeed";
import Themes from "@/pages/Themes";
import Consensus from "@/pages/Consensus";
import Implications from "@/pages/Implications";
import Sources from "@/pages/Sources";
import AIConsole from "@/pages/AIConsole";
import AdoptionJourney from "@/pages/AdoptionJourney";
import EvidenceLandscape from "@/pages/EvidenceLandscape";
import EvidenceAnalysis from "@/pages/EvidenceAnalysis";
import CongressIntel from "@/pages/CongressIntel";
import CompetitiveIntel from "@/pages/CompetitiveIntel";
import EmergingSignals from "@/pages/EmergingSignals";
import Reports from "@/pages/Reports";
import AdminReview from "@/pages/AdminReview";
import IntelligenceQuestions from "@/pages/IntelligenceQuestions";
import Calibration from "@/pages/Calibration";
import Integrity from "@/pages/Integrity";
import InformationModel from "@/pages/InformationModel";
import { AboutPagePanel } from "@/components/AboutPagePanel";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Site from "@/site/Site";

const TITLES = {
  dashboard: ["Executive Intelligence", "Calibrated global HCP intelligence on elacestrant (ORSERDU) in ESR1-mutated ER+/HER2- advanced breast cancer"],
  questions: ["Intelligence Questions", "Fifteen calibrated intelligence questions covering HCP voice, evidence interpretation, adoption barriers, regional differences and strategic actions"],
  feed: ["HCP Voice", "The collective HCP voice, backed by source-linked individual statements"],
  themes: ["Clinical Themes", "Explore the clinical topics shaping HCP perceptions, treatment behaviour and strategic priorities"],
  adoption: ["Adoption Journey", "Explore the questions, barriers and evidence needs shaping HCP adoption — from awareness to continued prescribing and peer recommendation"],
  evidence: ["Evidence Landscape", "Explore how scientific evidence supports, challenges or leaves uncertainty around the collective HCP voice"],
  "evidence-upload": ["Evidence Upload & Analysis", "Upload completed literature reviews and supporting sources — AI extracts, experts review, evidence connects to the HCP voice"],
  congress: ["Congress Intelligence", "Captured congress intelligence and the upcoming scientific-meeting watchlist"],
  competitive: ["Competitive Intelligence", "Evidence-calibrated comparison of elacestrant and relevant current or emerging treatment alternatives"],
  consensus: ["Consensus & Controversies", "Where HCP perspectives converge, diverge or remain uncertain — and how those positions relate to the available evidence"],
  signals: ["Emerging Signals", "Potential new or under-recognised observations prioritised for scientific review, corroboration and monitoring"],
  strategy: ["Strategic Implications", "Actionable takeaways for Menarini per pharmaceutical function"],
  sources: ["Source Library", "The governed evidence repository supporting every HCP statement, scientific finding and strategic interpretation"],
  reports: ["Reports", ""],
  admin: ["Admin & Review", "Project configuration, attribution governance and scientific-review calibration"],
  calibration: ["Calibration & Scoring", "Transparent, configurable scientific scoring — every confidence rating opens into its components and rationale"],
  integrity: ["Data Integrity & Compliance", "Operational controls, integrity checks, exceptions and audit evidence across the intelligence lifecycle"],
  model: ["Governed Information Model", "The canonical registry of linked objects behind every count — with stable identifiers, linkage integrity checks and full totals reconciliation"],
  ai: ["AI Analyst Console", "Ask the data — source-backed synthesis in real time"],
  analytics: ["Site Analytics", "Privacy-friendly first-party usage analytics — traffic, conversions, sources and devices"],
};

const FILTERED_VIEWS = ["feed", "evidence", "congress"];

function IntelligenceApp({ user, onLogout }) {
  const { view, filters, roleId } = useLens();
  const setView = lensStore.setView;
  const setFilters = lensStore.setFilters;
  const [project, setProject] = useState(null);
  const [aiPrefill, setAiPrefill] = useState(null);
  const role = getRole(roleId);

  const navigate = (id) => {
    if (!visibleIds(role).includes(id)) return;
    setView(id);
    window.history.pushState({ view: id }, "", `#${id}`);
  };

  useEffect(() => {
    const onPop = (e) => { if (e.state?.view && visibleIds(role).includes(e.state.view)) setView(e.state.view); };
    window.addEventListener("popstate", onPop);
    const initial = window.location.hash.replace("#", "");
    if (initial && visibleIds(role).includes(initial)) setView(initial);
    else window.history.replaceState({ view }, "", `#${view}`);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isGov = GOVERNANCE_GROUP.items.some((i) => i.id === view);
    track("pageview", isGov ? `/admin/${view}` : `/${view}`);
  }, [view]);

  const changeRole = (id) => {
    const cleared = lensStore.setRole(id);
    const r = getRole(id);
    const next = lensStore.get().view;
    if (next !== view) window.history.pushState({ view: next }, "", `#${next}`);
    const target = visibleIds(r).includes(r.defaultView) ? r.defaultView : "dashboard";
    toast.success(
      `Viewing as ${r.label} — ${cleared.length ? `cleared ${cleared.join("; ")}` : "page preserved"}`,
      { action: { label: "Open role dashboard", onClick: () => { setView(target); window.history.pushState({ view: target }, "", `#${target}`); } } },
    );
  };

  useEffect(() => {
    api.get("/projects/active").then((r) => setProject(r.data));
  }, []);

  const exploreTheme = (themeId) => {
    setFilters((f) => ({ ...f, theme_id: themeId }));
    setView("feed");
  };

  const [title, subtitle] = TITLES[view];

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      <Toaster position="top-right" richColors />
      <div className="flex">
        <Sidebar active={view} onNavigate={navigate} role={role} project={project} />
        <div className="flex-1 min-w-0">
          <MobileNav active={view} onNavigate={navigate} role={role} />
          <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px]">
            <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="flex-1 min-w-0">
                {view === "reports" ? (
                  <div className="bg-white border border-slate-200/80 rounded-xl px-4 py-2.5 shadow-sm flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="compact-project-bar"
                    title="ER+/HER2−, ESR1-mutated advanced or metastatic breast cancer following at least one line of endocrine therapy">
                    <span className="text-xs font-bold text-slate-900">Elacestrant Global HCP Intelligence Assessment</span>
                    {["ORSERDU · Elacestrant", "Global", "Cross-sectional", "Evidence cut-off: 14 Jun 2026", "Scientific review: In Review"].map((t) => (
                      <span key={t} className="text-[10.5px] text-slate-500">{t}</span>
                    ))}
                  </div>
                ) : <ProjectHeader project={project} />}
              </div>
              <div className="flex items-start gap-2">
                <RoleSwitcher roleId={roleId} onChange={changeRole} />
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="user-menu-btn" title={user?.email}
                      className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-teal-400 text-[11px] font-bold">
                      {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-3" data-testid="user-menu">
                    <p className="text-xs font-bold text-slate-800">{user?.name}</p>
                    <p className="text-[10.5px] text-slate-500">{user?.email}</p>
                    <button onClick={onLogout} data-testid="logout-button"
                      className="mt-2.5 w-full h-8 flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-200 text-[11px] font-semibold">
                      <LogOut className="w-3.5 h-3.5" />Sign out
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <header className="mb-6">
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
                <span className="ml-auto text-[8.5px] font-mono uppercase tracking-widest text-amber-600/80 border border-amber-200 rounded-full px-2 py-0.5 shrink-0 self-start mt-1.5" data-testid="prototype-badge" title="All content is a curated demonstration dataset — not for real-world decision-making">
                  Illustrative prototype
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            </header>

            {view !== "reports" && <AboutPagePanel view={view} key={view} />}
            {FILTERED_VIEWS.includes(view) && (
              <div className="mb-6">
                <FilterBar filters={filters} setFilters={setFilters} />
              </div>
            )}
            {view === "dashboard" && <Dashboard role={role} onOpenEvidence={() => setView("feed")} />}
            {view === "questions" && <IntelligenceQuestions onNavigate={setView} onAskAI={(q) => { setAiPrefill(q); setView("ai"); }} />}
            {view === "feed" && <InsightFeed filters={filters} setFilters={setFilters} role={role} />}
            {view === "themes" && <Themes onExploreTheme={exploreTheme} onExploreTaxonomy={(id) => { setFilters({ taxonomy_id: id }); setView("feed"); }} />}
            {view === "adoption" && <AdoptionJourney role={role} onOpenEvidence={() => setView("feed")} />}
            {view === "evidence" && <EvidenceLandscape globalFilters={filters} onOpenVoice={() => setView("feed")} />}
            {view === "evidence-upload" && <EvidenceAnalysis />}
            {view === "congress" && <CongressIntel globalFilters={filters} />}
            {view === "competitive" && <CompetitiveIntel />}
            {view === "consensus" && <Consensus />}
            {view === "signals" && <EmergingSignals onOpenInsight={() => setView("feed")} />}
            {view === "strategy" && <Implications role={role} />}
            {view === "sources" && <Sources role={role} onOpenInsight={() => setView("feed")} />}
            {view === "reports" && <Reports role={role} onNavigate={(v) => { setView(v); window.history.pushState({ view: v }, "", `#${v}`); }} />}
            {view === "admin" && (role.group === "Platform" ? <AdminReview /> : (
              <div className="bg-white border border-slate-200/80 rounded-xl p-10 text-center shadow-sm" data-testid="rbac-restricted">
                <p className="font-heading text-base font-bold text-slate-900">Access restricted</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">The administration and expert-review workspace is limited to Platform roles (Platform Administrator, MarksMan Scientific Reviewer). Switch your role lens to gain access.</p>
              </div>
            ))}
            {view === "integrity" && <Integrity />}
            {view === "model" && <InformationModel />}
            {view === "calibration" && <Calibration />}
            {view === "analytics" && <Analytics />}
            {view === "ai" && <AIConsole role={role} prefill={aiPrefill} onPrefillConsumed={() => setAiPrefill(null)} />}
          </main>
        </div>
      </div>
    </div>
  );
}

function LoginRoute() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false)).finally(() => setChecking(false));
  }, []);
  if (checking) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 text-sm">Checking session…</div>;
  if (authed) return <Navigate to="/app" replace />;
  return <Login onLogin={() => nav("/app", { replace: true })} />;
}

function AuthGate() {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = () => {
    api.post("/auth/logout").finally(() => setUser(false));
  };
  if (user === null) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 text-sm">Checking session…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <IntelligenceApp user={user} onLogout={logout} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/app/*" element={<AuthGate />} />
        <Route path="/*" element={<Site />} />
      </Routes>
    </BrowserRouter>
  );
}
